import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertTriangle, CheckCircle, ClipboardPaste } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FORMATS = {
  hours: 'employee|date|rate|time_range|scheduled|paid_hours|est_wages',
  sales: 'employee|date|service_lines|service_sales|zero_price_service_lines|retail_units|retail_sales|retail_commission|tips_gross|prepaid_sales',
  refunds: 'employee|date|order|item|line_subtotal|line_tip|refund_amount',
  adp: 'check_date|adp_name|reg_hours|reg_rate|regular_pay|ot_hours|ot_pay|vacation_hours|vacation_pay|tips|service_commission|retail_commission_misc_pay|reimbursement_nontaxable|hra|c1099_hours|c1099_detail|c1099_total|gross|employee_taxes|net|employer_taxes',
  pto: 'check_date|adp_name|pto_hours_available|pto_hours_used',
};

const parseExpected = (txt) => {
  const out = {};
  (txt || '').split('\n').forEach((l) => {
    const [k, v] = l.split('=').map((x) => (x || '').trim());
    if (k && v !== undefined && v !== '') out[k] = Number(v.replace(/[$,]/g, ''));
  });
  return out;
};

const detailText = (d) => {
  if (!d) return 'Request failed';
  if (typeof d === 'string') return d;
  return [d.message, (d.names || []).join(', '), (d.blockers || []).join('; ')].filter(Boolean).join(' ');
};

const KV = ({ obj, testid }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid={testid}>
    {Object.entries(obj || {}).map(([k, v]) => (
      <div key={k} className="rounded border border-stone-200 bg-white px-3 py-2">
        <div className="text-[11px] uppercase tracking-wide text-stone-500">{k.replace(/_/g, ' ')}</div>
        <div className="text-sm font-semibold text-stone-900" data-testid={`${testid}-${k}`}>{String(v)}</div>
      </div>
    ))}
  </div>
);

const PerDateTable = ({ perDate }) => (
  !Object.keys(perDate || {}).length ? (
    <p className="text-sm text-stone-500" data-testid="text-import-per-date-empty">No row changes planned yet — fix the blockers above to see rows to delete and insert per date.</p>
  ) : (
  <table className="w-full text-sm" data-testid="text-import-per-date">
    <thead><tr className="text-left text-stone-500"><th className="py-1">Date</th><th>Delete</th><th>Insert</th></tr></thead>
    <tbody>
      {Object.entries(perDate || {}).map(([d, v]) => (
        <tr key={d} className="border-t border-stone-100" data-testid={`text-import-date-${d}`}>
          <td className="py-1 font-mono">{d}</td>
          <td>{Object.entries(v.delete).map(([c, n]) => `${c} ${n}`).join(' · ') || '0'}</td>
          <td>{Object.entries(v.insert).map(([c, n]) => `${c} ${n}`).join(' · ') || '0'}</td>
        </tr>
      ))}
    </tbody>
  </table>
  )
);

const ByEmployee = ({ rows }) => (
  <table className="w-full text-sm" data-testid="text-import-by-employee">
    <tbody>
      {Object.entries(rows || {}).map(([name, t]) => (
        <tr key={name} className="border-t border-stone-100 align-top">
          <td className="py-1 pr-3 font-medium whitespace-nowrap">{name}</td>
          <td className="py-1 text-stone-700">{Object.entries(t).map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`).join(' · ')}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Notices = ({ p }) => {
  const items = [
    ...(p.blockers || []).map((b) => ['block', b]),
    ...(p.mismatches || []).map((m) => ['block', `${m.employee ? m.employee + ' · ' : ''}${m.key}: expected ${m.expected}, got ${m.got}`]),
    ...(p.errors || []).map((e) => ['block', `Line ${e.line}: ${e.reason}`]),
    ...(p.row_check_errors || []).map((e) => ['block', `Line ${e.line}: ${e.employee} ${e.date}: ${e.reason}`]),
    ...(p.off_cycle || []).map((o) => ['note', `Off-cycle check: ${o.employee} ${o.check_date} ($${o.gross})`]),
    ...(p.outside_period || []).map((o) => ['block', `Line ${o.line}: ${o.employee} ${o.date} is outside this period`]),
    ...(p.excluded_names || []).map((n) => ['note', `${n}: excluded, not stored`]),
    ...(p.non_roster_retained || []).map((n) => ['note', `${n}: period totals only, never attributed`]),
    ...(p.admin_only_names || []).map((n) => ['note', `${n}: stored admin-only (${p.admin_only_rows} rows)`]),
    ...(p.open_punches || []).map((o) => ['note', `Open punch skipped: ${o.employee} ${o.date}`]),
    ...(p.no_punch_rows || []).map((o) => ['note', `Scheduled, no punch (0 hrs, not a worked date): ${o.employee} ${o.date}`]),
  ];
  if (!items.length) return null;
  return (
    <ul className="space-y-1 text-sm" data-testid="text-import-notices">
      {items.map(([t, m], i) => (
        <li key={i} className={t === 'block' ? 'text-red-700' : 'text-stone-600'}>{t === 'block' ? 'Blocked: ' : ''}{m}</li>
      ))}
    </ul>
  );
};

export default function TextImportPage() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [kind, setKind] = useState('hours');
  const [text, setText] = useState('');
  const [expected, setExpected] = useState('');
  const [preview, setPreview] = useState(null);
  const [importId, setImportId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetch(`${API}/api/payroll-periods`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setPeriods(d.filter((p) => !['finalized', 'locked'].includes(p.status) && !p.is_locked)))
      .catch(() => setError('Failed to load periods'));
  }, []);

  const reset = () => { setPreview(null); setImportId(null); setError(''); setSuccess(''); };

  const runPreview = async () => {
    setBusy(true); reset();
    try {
      const res = await fetch(`${API}/api/admin/import/text/preview`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, period_id: periodId, text, expected: parseExpected(expected) }),
      });
      const data = await res.json();
      if (res.ok) { setPreview(data.preview); setImportId(data.import_id); } else setError(detailText(data.detail));
    } catch (e) { setError('Preview failed'); } finally { setBusy(false); }
  };

  const runConfirm = async () => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/api/admin/import/text/confirm/${importId}`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        const w = Object.entries(data.rows_written).filter(([, n]) => n).map(([c, n]) => `${c} ${n}`).join(', ');
        setSuccess(`Import confirmed. Written: ${w || 'nothing'}. Data check started.`);
        setPreview(null); setImportId(null); setText('');
      } else setError(detailText(data.detail));
    } catch (e) { setError('Confirm failed'); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50 p-6" data-testid="text-import-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-serif font-bold text-stone-900">Paste Import</h1>
          <p className="text-stone-600 mt-2">Paste pipe-delimited rows. Nothing is saved until you confirm the preview.</p>
        </div>
        {error && <Alert variant="destructive" data-testid="text-import-error"><AlertTriangle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
        {success && <Alert className="border-green-200 bg-green-50" data-testid="text-import-success"><CheckCircle className="h-4 w-4 text-green-600" /><AlertDescription className="text-green-800">{success}</AlertDescription></Alert>}

        <Card>
          <CardHeader>
            <CardTitle>1 · What are you pasting?</CardTitle>
            <CardDescription>Columns: <span className="font-mono text-xs break-all" data-testid="text-import-format">{FORMATS[kind]}</span></CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {Object.keys(FORMATS).map((k) => (
                <Button key={k} variant={kind === k ? 'default' : 'outline'} className={kind === k ? '' : 'border-stone-400 bg-white text-stone-800'} onClick={() => { setKind(k); reset(); }} data-testid={`text-import-kind-${k}`}>
                  {k[0].toUpperCase() + k.slice(1)}
                </Button>
              ))}
            </div>
            <select value={periodId} onChange={(e) => { setPeriodId(e.target.value); reset(); }} className="w-full border border-stone-300 rounded-md px-3 py-2" data-testid="text-import-period-select">
              <option value="">Select period…</option>
              {periods.map((p) => (
                <option key={p._id} value={p._id}>{p.period_number ? `P${p.period_number} · ` : ''}{p.start_date} → {p.end_date} ({p.status})</option>
              ))}
            </select>
            <Textarea rows={12} value={text} onChange={(e) => { setText(e.target.value); reset(); }} placeholder={FORMATS[kind]} className="font-mono text-xs" data-testid="text-import-textarea" />
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Expected totals (optional, one per line, e.g. paid_hours=512.25)</label>
              <Textarea rows={3} value={expected} onChange={(e) => { setExpected(e.target.value); reset(); }} className="font-mono text-xs" data-testid="text-import-expected" />
            </div>
            <Button onClick={runPreview} disabled={busy || !periodId || !text.trim()} data-testid="text-import-preview-btn">
              <ClipboardPaste className="mr-2 h-4 w-4" /> Preview
            </Button>
          </CardContent>
        </Card>

        {preview && (
          <Card data-testid="text-import-preview">
            <CardHeader>
              <CardTitle>2 · Preview — {preview.rows_parsed} rows · {preview.period.start_date} → {preview.period.end_date}</CardTitle>
              <CardDescription data-testid="text-import-can-confirm">{preview.can_confirm ? 'Ready to confirm.' : 'Cannot confirm until every blocker is fixed.'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <Notices p={preview} />
              <KV obj={preview.totals} testid="text-import-totals" />
              <ByEmployee rows={preview.totals_by_employee} />
              <PerDateTable perDate={preview.per_date} />
              <div className="flex gap-2">
                <Button onClick={runConfirm} disabled={busy || !preview.can_confirm} data-testid="text-import-confirm-btn">Confirm import</Button>
                <Button variant="outline" onClick={reset} data-testid="text-import-cancel-btn">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
